<script setup lang="ts">
import type { ExtensionSummary } from '#layers/extensions/types/catalog';
import { computed } from 'vue';

const props = defineProps<{
	extensions: ExtensionSummary[];
}>();

const extensionLists = computed(() => props.extensions.map(({ id, name, description, icon, iconColor, entries }) => ({
	id,
	items: [
		{
			id: `bundle:${id}`,
			displayId: id,
			name,
			description,
			icon: icon || 'lucide:blocks',
			iconColor,
			typeLabelKey: undefined,
		},
		...entries.map(({ id, name, description, icon, iconColor, type }) => ({
			id,
			displayId: id,
			name,
			description,
			icon,
			iconColor,
			typeLabelKey: `extensions.entry_types.${type}`,
		})),
	],
})));
</script>

<template>
	<DEmpty
		v-if="extensions.length === 0"
		icon="lucide:blocks"
		:title="$t('extensions.empty_title')"
		:description="$t('extensions.empty_description')"
	/>
	<div v-else class="space-y-4">
		<DList
			v-for="extension in extensionLists"
			:key="extension.id"
			:items="extension.items"
			label-key="name"
			class="bg-default"
			:ui="{ item: { description: 'whitespace-normal', trailing: 'shrink-0 pl-3' } }"
		>
			<template #item-leading="{ item }">
				<DIcon :name="item.icon" :style="{ color: item.iconColor }" class="size-5 text-muted" />
			</template>
			<template #item-description="{ item }">
				<span class="block font-mono break-all">{{ item.displayId }}</span>
				<span v-if="item.description" class="mt-1 block">{{ item.description }}</span>
			</template>
			<template #item-trailing="{ item }">
				<DBadge v-if="item.typeLabelKey" color="neutral" variant="subtle">
					{{ $t(item.typeLabelKey) }}
				</DBadge>
			</template>
		</DList>
	</div>
</template>
